import { useMemo, useState } from 'react';

import { CLICK_IDS } from '@sunflower-games/analytics-events';
import { computed } from 'mobx';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { useEffectOnce } from 'react-use';

import Button from '../../../components/buttons/Button';
import { LANGUAGES_OPTIONS } from '../../../components/ChangeLanguage/constants';
import { CountrySelect } from '../../../components/CountrySelect/CountrySelect';
import { DialogBox } from '../../../components/Dialog/DialogBox';
import { DialogContent } from '../../../components/Dialog/DialogContent';
import { DialogHeader } from '../../../components/Dialog/DialogHeader';
import { DialogLoader } from '../../../components/Dialog/DialogLoader';
import { ErrorMessage } from '../../../components/ErrorMessage/ErrorMessage';
import { LanguageSelect } from '../../../components/LanguageSelect/LanguageSelect';
import type { LanguageOption } from '../../../components/LanguageSelect/types';
import { getPhoneErrorMessageMap } from '../../../components/PhoneNumberInput/constants';
import type { WizardChildProps } from '../../../components/Wizard/types';
import { useLocale } from '../../../hooks/useLocale';
import i18n from '../../../i18n/config';
import { analytics } from '../../../services/analytics';
import { useFlag } from '../../../services/launchDarkly';
import { useStores } from '../../../stores/useStores';
import { getErrorMessage } from '../../../utils/errors';
import { useCountriesData } from '../../geography/api/countries';
import type { Country } from '../../geography/types';

import styles from './ChooseCountry.module.scss';

interface Props extends Partial<WizardChildProps> {
  componentType?: 'dialog' | 'portal';
}
const ChooseCountry = observer(function ChooseCountry({ componentType = 'portal' }: Props) {
  const { portalStore, authStore, uiStore, userStore } = useStores();
  const [fullCountriesData] = useCountriesData();
  const isLanguageChangeAvailable = useFlag('language_change_available', false);

  const { t } = useTranslation();
  const { isGDPR } = useLocale();

  const [error, setError] = useState<string>('');

  const isProcessing = computed(() => authStore.pending('chooseCountry')).get();

  const countryCode = userStore.profile?.cleanGeoData?.countryCode;

  const defaultCountry = useMemo(() => {
    if (countryCode) {
      return fullCountriesData.list.find((country) => country.code === countryCode) || null;
    }
    return null;
  }, [countryCode, fullCountriesData.list]);

  const [selectedCountry, setSelectedCountry] = useState<Country | null>(defaultCountry);

  const defaultLanguage = useMemo(() => {
    const languageId = i18n.language.toUpperCase();
    return LANGUAGES_OPTIONS.find((lang) => lang.id === languageId) || LANGUAGES_OPTIONS[0];
  }, []);

  const [selectedLanguage, setSelectedLanguage] = useState(defaultLanguage);

  const country = selectedCountry ?? defaultCountry;

  const chooseCountry = async () => {
    try {
      if (!country) {
        return { success: false, message: getPhoneErrorMessageMap().missingCountryError };
      }

      const result = await authStore.chooseCountry({ countryCode: country.code, language: selectedLanguage.id });

      if (!result.errors?.length) {
        return { success: true, message: '' };
      }
      return { success: false, message: String(result.errors[0]) };
    } catch (err) {
      const msg = getErrorMessage(err);
      return { success: false, message: msg };
    }
  };

  const onChangeLanguage = (newValue: LanguageOption) => {
    setSelectedLanguage(newValue);
    void i18n.changeLanguage(newValue.id);
  };

  const onSubmit = async () => {
    setError('');

    void analytics.sendEvent('sign_up', {
      event_action: 'click',
      step_name: 'locale_settings',
      selected_country: country?.code || null,
      selected_language: selectedLanguage.id,
      click_id: CLICK_IDS.AUTH_FLOW.CHOOSE_COUNTRY_DIALOG,
    });

    const { success, message } = await chooseCountry();

    if (!success) {
      setError(message);
      return;
    }

    if (componentType === 'portal') {
      portalStore.closePortal();
    } else {
      uiStore.setOpenDialog({ dialog: null });
    }
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void onSubmit();
  };

  useEffectOnce(() => {
    void analytics.sendEvent('sign_up', {
      event_action: 'impression',
      step_name: 'locale_settings',
    });
  });

  const sweepsPolicyUrl = isGDPR ? t('sweeps.policy.superUrl') : t('sweeps.policy.url');

  return (
    <DialogBox className={styles.chooseCountryBox}>
      <DialogHeader className={styles.chooseCountryTitle} title={t('settings.chooseYourSettings')} />

      <DialogLoader isProcessing={isProcessing} />

      <DialogContent className={styles.chooseCountryContainer}>
        <form onSubmit={handleSubmit}>
          <div className="form-field">
            <label className="form-field__label" htmlFor="country">
              {t('forms.country')}
            </label>
            <CountrySelect
              name="country"
              options={fullCountriesData.list}
              value={country}
              onChange={(newValue) => {
                setSelectedCountry(newValue);
              }}
            />
            {isLanguageChangeAvailable && (
              <>
                <label className="form-field__label" htmlFor="language">
                  {t('user.profile.languageUpperCase')}
                </label>
                <LanguageSelect
                  customStyles={{
                    languageName: {
                      marginLeft: '0.6rem',
                    },
                  }}
                  name="language"
                  options={LANGUAGES_OPTIONS}
                  value={selectedLanguage}
                  onChange={(newValue) => newValue && onChangeLanguage(newValue)}
                />
              </>
            )}
          </div>

          <p className={styles.privacyAcceptText}>
            {t('legal.consentPrefix')}{' '}
            <a href={`/pages/${sweepsPolicyUrl}`} rel="noreferrer" target="_blank">
              {t('sweeps.policy.title', { sweepsOrSuper: !isGDPR })}
            </a>
          </p>

          <Button
            btnType="main"
            className={styles.submitButton}
            dataTestId="choose-country-done-btn"
            disabled={!country}
            type="submit"
          >
            {t('poc.doneUpperCase')}
          </Button>
          <div className={styles.hint}>{t('auth.countryCurrencyNote')}</div>
          <div className={styles.hint}>{t('auth.languageChangeNote')}</div>
        </form>
        <ErrorMessage>{error}</ErrorMessage>
      </DialogContent>
    </DialogBox>
  );
});

export default ChooseCountry;
